import { Contract } from '@ethersproject/contracts'
import { Web3Provider } from '@ethersproject/providers'
import { getAddress } from '@ethersproject/address'
import { formatUnits, formatEther } from '@ethersproject/units'
import WalletConnectProvider from '@walletconnect/web3-provider'
import { useToast } from 'vue-toastification'
import localStorageUtils from '@/utils/localstorage.js'
import {
  VITA_MAX_APPROVE_AMOUNT,
  VITA_TOKEN_DECIMALS,
  ETHEREUM_MAX_INTEGER,
} from '@/utils/constants'

import tokenABI from '@/abis/PHARM.json'

// Initial state
const state = () => ({
  isConnected: localStorageUtils.read('walletIsConnected'),
  walletProvider: localStorageUtils.read('walletProvider') || null,
  connectionInfo: localStorageUtils.read('connectionInfo') || {
    address: null,
    chainId: null,
  },
  connectWalletModalIsOpen: false,
  loadingUserDetails: false,
  ethBalance: 0.0,
  ensName: null,
  vitaIsApproved: false,
  approvingVita: false,
  vitaBalance: 0.0,
  stakedVitaBalance: 0.0,
  stakingVita: false,
  unstakingVita: false,
  isVoting: false,
  stakeUnlockBlock: null,
  lastVotedProposalId: null,
  currentBlock: 0,
})

const chainIds = [
  {
    id: 1,
    name: 'mainnet',
  },
  {
    id: 3,
    name: 'ropsten',
  },
  {
    id: 5,
    name: 'goerli',
  },
  {
    id: 10,
    name: 'optimism',
  },
  {
    id: 100,
    name: 'xdai',
  },
  {
    id: 137,
    name: 'polygon',
  },
  {
    id: 42161,
    name: 'arbitrum',
  },
]

const toast = useToast()

let ethersInstance
let signer
let tokenContract
let tokenContractWithSigner

// getters
const getters = {
  currentNetwork: (state) => {
    let chain = chainIds.find((n) => n.id === state.connectionInfo.chainId)
    if (chain) {
      return chain
    } else {
      return {
        id: 0,
        name: 'unknown',
      }
    }
  },
  correctNetwork: () => {
    return chainIds.find((n) => n.id === parseInt(process.env.VUE_APP_ETHEREUM_CHAIN_ID))
  },
  getNetworkById: () => (id) => {
    return chainIds.find((n) => n.id === id)
  },
  isWrongNetwork: (state, getters) => {
    return (
      state.isConnected &&
      parseInt(getters['currentNetwork'].id) !== parseInt(process.env.VUE_APP_ETHEREUM_CHAIN_ID)
    )
  },
  isTransacting: (state) => {
    return state.approvingVita || state.stakingVita || state.unstakingVita || state.isVoting
  },
}

// actions
const actions = {
  async initUser({ commit, state, dispatch }) {
    const network = await ethersInstance.getNetwork()
    commit('setChainId', network.chainId)

    // Get ENS Name if available
    const ensName = await ethersInstance.lookupAddress(state.connectionInfo.address)
    commit('setEnsName', ensName)

    tokenContract = new Contract(
      process.env.VUE_APP_VITA_TOKEN_ADDRESS,
      tokenABI.abi,
      ethersInstance,
    )
    tokenContractWithSigner = tokenContract.connect(signer)

    await dispatch('updateUserBalances')

    ///// ////////
    /// CONTRACT EVENTS
    //////////////

    let incomingFilter = tokenContract.filters.Transfer(null, state.connectionInfo.address)
    let outgoingFilter = tokenContract.filters.Transfer(state.connectionInfo.address)

    // Listen for incoming VITA transactions (e.g. stake withdraw)
    tokenContract.on(incomingFilter, (from, to, amount) => {
      dispatch('updateUserBalances')
      console.log(`I got ${amount} from ${from}.`)
      if (getAddress(from) == getAddress(process.env.VUE_APP_STAKING_CONTRACT_ADDRESS)) {
        commit('setUnstakingVita', false)
        toast.success('Unstaking was successful', {
          timeout: 7500,
        })
      }
    })

    // Listen for outgoing VITA transactions (e.g. staking)
    tokenContract.on(outgoingFilter, (from, to, amount) => {
      dispatch('updateUserBalances')
      console.log(`I sent ${amount} to ${to}.`)
      if (getAddress(to) == getAddress(process.env.VUE_APP_STAKING_CONTRACT_ADDRESS)) {
        commit('setStakingVita', false)
        toast.success('Staking was successful', {
          timeout: 7500,
        })
      }
    })

    ///// ////////
    /// GLOBAL CHAIN EVENTS
    //////////////

    // Listen for new ethereum blocks
    ethersInstance.on('block', (blockNumber) => {
      // console.log('New block:', blockNumber)
      dispatch('updateUserBalances')
      commit('setCurrentBlock', blockNumber.toString())
    })

    // Reload page when chain was changed
    ethersInstance.provider.on('chainChanged', (chainId) => {
      console.log(`chain changed to ${chainId}`)
      window.location.reload()
      // let newChain = ethers.BigNumber.from(chainId).toNumber()
      // if (parseInt(newChain) != parseInt(process.env.VUE_APP_ETHEREUM_CHAIN_ID)) {
      //   window.location.reload()
      // } else {
      //   window.location.reload()
      // }
    })

    // Subscribe to session disconnection
    ethersInstance.on('disconnect', (code, reason) => {
      console.log('provider disconnect:', code, reason)
    })

    // Reload page if connected account was changed
    ethersInstance.provider.on('accountsChanged', function (accounts) {
      console.log('accountsChanged:', accounts[0])
      window.location.reload()
    })
  },
  async updateUserBalances({ commit, state }) {
    const ethBalance = await ethersInstance.getBalance(state.connectionInfo.address)
    commit('setEthBalance', formatEther(ethBalance))

    try {
      const vitaBalance = await tokenContract.balanceOf(state.connectionInfo.address)
      commit('setVitaBalance', formatUnits(vitaBalance, VITA_TOKEN_DECIMALS))
    } catch {
      commit('setVitaBalance', 0)
    }
  },
  async connectInjected({ commit, dispatch }) {
    let accounts
    try {
      accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      })
      commit('setProvider', 'injected')
      commit('setAddress', accounts[0])
      ethersInstance = new Web3Provider(window.ethereum)

      signer = ethersInstance.getSigner()

      commit('setLoadingUserDetails', true)
      await dispatch('initUser')

      commit('connect')
      commit('setLoadingUserDetails', false)
    } catch (e) {
      console.log('user rejected or other error', e)
    }
  },
  async connectWalletConnect({ commit, dispatch }) {
    //  Create WalletConnect Provider
    const provider = new WalletConnectProvider({
      infuraId: process.env.VUE_APP_INFURA_API_KEY,
    })

    //  Enable session (triggers QR Code modal)
    await provider.enable()

    ethersInstance = new Web3Provider(provider)
    signer = ethersInstance.getSigner()
    let accounts = await ethersInstance.listAccounts()
    commit('setProvider', 'walletconnect')
    commit('setAddress', accounts[0])

    await dispatch('initUser')

    commit('connect')
  },
  async disconnect({ commit }) {
    // Remove localstorage data from external WalletConnect library
    localStorage.removeItem(`walletconnect`)
    commit('disconnect')
    commit('removeProvider')
    commit('removeChainInfo')
    window.location.reload()
  },
  async approveVitaToken({ commit }) {
    commit('setApprovingVita', true)
    await tokenContractWithSigner.approve(
      process.env.VUE_APP_STAKING_CONTRACT_ADDRESS,
      ETHEREUM_MAX_INTEGER,
    )
  },
}

// mutations
const mutations = {
  connect(state) {
    localStorageUtils.write('walletIsConnected', true)
    state.isConnected = true
  },
  disconnect(state) {
    localStorageUtils.remove('walletIsConnected')
    state.isConnected = false
  },
  setProvider(state, value) {
    localStorageUtils.write('walletProvider', value)
    state.walletProvider = value
  },
  removeProvider(state) {
    localStorageUtils.remove('walletProvider')
    state.walletProvider = null
  },
  setAddress(state, value) {
    state.connectionInfo.address = value
    localStorageUtils.write('connectionInfo', state.connectionInfo)
  },
  setChainId(state, value) {
    state.connectionInfo.chainId = value
    localStorageUtils.write('connectionInfo', state.connectionInfo)
  },
  removeChainInfo(state) {
    state.connectionInfo = {
      address: null,
      chainId: null,
    }
    localStorageUtils.remove('connectionInfo')
  },
  setEthBalance(state, value) {
    state.ethBalance = value
  },
  setEnsName(state, value) {
    state.ensName = value
  },
  setVitaBalance(state, value) {
    state.vitaBalance = value
  },
  setStakedVitaBalance(state, value) {
    state.stakedVitaBalance = value
  },
  setVitaIsApproved(state, value) {
    state.vitaIsApproved = value
  },
  setApprovingVita(state, value) {
    state.approvingVita = value
  },
  setStakingVita(state, value) {
    state.stakingVita = value
  },
  setUnstakingVita(state, value) {
    state.unstakingVita = value
  },
  setLoadingUserDetails(state, value) {
    state.loadingUserDetails = value
  },
  setConnectWalletModalIsOpen(state, value) {
    state.connectWalletModalIsOpen = value
  },
  setIsVoting(state, value) {
    state.isVoting = value
  },
  setLastVotedProposalId(state, id) {
    state.lastVotedProposalId = id
  },
  setStakeUnlockBlock(state, block) {
    state.stakeUnlockBlock = block
  },
  setCurrentBlock(state, block) {
    state.currentBlock = block
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
